import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, Tab } from '@mui/material';
import Spinner from 'components/Spinner';
import { TypographyLocale } from 'components/TypographyLocale';
import UseAuthContext from 'context/AuthContext';
import UseLanguageContext from 'context/LanguageContext';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { GetUserOrganization, GetUserOrganizationShort } from 'services/auth-service';
import { USER_ROLES } from 'utils/constants';
import BusinessContact from './BusinessContact';
import BusinessCredits from './BusinessCredits';
import BusinessPackages from './BusinessPackages';
import BusinessSetting from './BusinessSetting';
import BusinessStaff from './BusinessStaff';

const hasAnySubscription = (organization: any): boolean => {
  if (!organization?.transactions) return false;
  const transactions = Object.values(organization.transactions || {}) as any[];
  return transactions.some((t: any) => t.unlimited === true && t.type === 'CREDIT');
};

const BusinessAccountPage = ({ mode }) => {
  const [value, setValue] = useState('SETTING');
  const { t } = useTranslation();
  const { language } = useContext(UseLanguageContext);
  const navigate = useNavigate();
  const { setOrganization, organizations ,role} = useContext(UseAuthContext);
  const { data: dataOrganization, isLoading, refetch } = useQuery({
    queryKey: ['getOrganizationById', organizations?.id],
    queryFn: () => GetUserOrganization(organizations?.id),
    enabled: !!organizations?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,

  })
  const isInspectorRole = role === USER_ROLES.INSPECTOR || role === USER_ROLES.INSPECTOR_MANAGER;
  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  
 
  if (isLoading && mode !== 'create') {
    return <Spinner></Spinner>;
  }


  const OrganizationCallback = async (organizationID: any) => {
    const OrganizationData = await GetUserOrganizationShort(organizationID);
    setOrganization(OrganizationData.data);
    // Better way to navigate once we've some info from Admin regarding new business account
    navigate(`/edit/business/${organizationID}`);
  };

  return (
    <Box sx={{}}>
      <Box sx={{ height: "144px", mb: "22px", display: "flex", alignItems: "center" }}>
        <TypographyLocale
          variant='h2'
          language={language}
        >
          {t('BUSINESS_EDIT_HEADER')}
        </TypographyLocale>
      </Box>
      <Box sx={{ bgcolor: '#fff' }}>
        <TabContext value={value}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: '30px' }}>
            <TabList onChange={handleChange} aria-label='lab API tabs example'>
              <Tab label={t('BUSINESS_EDIT_Setting_HEADER')} value='SETTING' />
              <Tab label={t('BUSINESS_EDIT_CONTACT_HEADER')} value='CONTACT' disabled={mode == "create"} />
              {!isInspectorRole && <Tab label={t('BUSINESS_EDIT_CREDIT_HEADER')} value='CREDITS' disabled={mode == "create"} />}
              {!isInspectorRole && (role === USER_ROLES.ADMIN || hasAnySubscription(dataOrganization)) && <Tab label={t('BUSINESS_EDIT_PACKAGES_HEADER')} value='PACKAGES' disabled={mode == "create"} />}
              <Tab label={t('BUSINESS_EDIT_STAFF_HEADER')} value='STAFF' disabled={mode == "create"} />
            </TabList>
          </Box>
          <TabPanel value='SETTING' className='tab-content'>
            <BusinessSetting 
              refetch={refetch} 
              mode={mode} 
              organizationCallback={OrganizationCallback}
              dataOrganization={mode == "create" ? null : dataOrganization}
            />
          </TabPanel>
          <TabPanel value='CONTACT' className='tab-content'>
            <BusinessContact organizations={dataOrganization} refetch={refetch} />
          </TabPanel>
          {!isInspectorRole && (
          <TabPanel value='CREDITS' className='tab-content' >
            <BusinessCredits
              organizations={dataOrganization}
              refetch={refetch}
              mode={mode}
            />
          </TabPanel>
          )}
          {!isInspectorRole && (role === USER_ROLES.ADMIN || hasAnySubscription(dataOrganization)) && (
          <TabPanel value='PACKAGES' className='tab-content' >
            <BusinessPackages
              organizations={dataOrganization}
              refetch={refetch}
              mode={mode}
            />
          </TabPanel>
          )}
          <TabPanel value='STAFF' className='tab-content' >
            <BusinessStaff refetch={refetch} dataOrganization={dataOrganization}/>
          </TabPanel>
        </TabContext>
      </Box>
    </Box>
  );
};

export default BusinessAccountPage;
